import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('rashii_user');
    const accessToken = localStorage.getItem('rashii_access_token');
    
    if (storedUser && accessToken) {
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch (err) {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem('rashii_access_token', access_token);
      localStorage.setItem('rashii_refresh_token', refresh_token);
      localStorage.setItem('rashii_user', JSON.stringify(userData));
      
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, refresh_token, user: newUser } = response.data;
      
      localStorage.setItem('rashii_access_token', access_token);
      localStorage.setItem('rashii_refresh_token', refresh_token);
      localStorage.setItem('rashii_user', JSON.stringify(newUser));
      
      setUser(newUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('rashii_access_token');
    localStorage.removeItem('rashii_refresh_token');
    localStorage.removeItem('rashii_user');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const refreshToken = useCallback(async () => {
    const refresh = localStorage.getItem('rashii_refresh_token');
    if (!refresh) return false;
    
    try {
      const response = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
      const { access_token, refresh_token: newRefresh, user: userData } = response.data;
      
      localStorage.setItem('rashii_access_token', access_token);
      localStorage.setItem('rashii_refresh_token', newRefresh);
      localStorage.setItem('rashii_user', JSON.stringify(userData));
      
      setUser(userData);
      return true;
    } catch {
      logout();
      return false;
    }
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, register, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
